import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/authContext";

export default function SignupPage() {
  const { signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const from = params.get("from") || "/boardroom";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (name.trim().length < 1) e.name = "Tell us what to call you.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) e.email = "Enter a valid email.";
    if (!/^(?=.*[A-Za-z])(?=.*\d).{8,128}$/.test(password))
      e.password = "8+ characters, must include a letter and a digit.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await signup(email.trim(), password, name.trim());
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not create account.";
      if (err?.response?.status === 409) {
        setErrors((s) => ({ ...s, email: detail }));
      } else {
        toast.error("Signup failed", { description: detail });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="signup-page"
      className="flex min-h-screen items-center justify-center bg-[#F7F7F5] px-5 py-16"
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="card-soft w-full max-w-md rounded-[28px] p-8"
      >
        <Link to="/" className="mb-7 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-zinc-900 to-black">
            <Sparkles className="h-4 w-4 text-white" strokeWidth={2.4} />
          </span>
          <span className="text-[15px] font-extrabold tracking-tight text-zinc-900">
            Idea AI
          </span>
        </Link>

        <h1 className="text-[32px] font-extrabold leading-tight tracking-tight text-zinc-900">
          Create your boardroom.
        </h1>
        <p className="mt-1.5 text-[13px] text-zinc-500">
          60 seconds. Then meet your eight executives.
        </p>

        <form onSubmit={onSubmit} className="mt-7 space-y-4" noValidate>
          <div>
            <label className="text-[12px] font-semibold uppercase tracking-[0.14em] text-zinc-600">
              Your name
            </label>
            <input
              data-testid="signup-name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-black/[0.08] bg-white px-4 py-3 text-[14px] text-zinc-900 outline-none transition-colors focus:border-zinc-900"
              placeholder="Jordan Maris"
            />
            {errors.name && (
              <div className="mt-1 text-[12px] text-rose-600" data-testid="signup-name-error">
                {errors.name}
              </div>
            )}
          </div>
          <div>
            <label className="text-[12px] font-semibold uppercase tracking-[0.14em] text-zinc-600">
              Email
            </label>
            <input
              data-testid="signup-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-black/[0.08] bg-white px-4 py-3 text-[14px] text-zinc-900 outline-none transition-colors focus:border-zinc-900"
              placeholder="you@example.com"
            />
            {errors.email && (
              <div className="mt-1 text-[12px] text-rose-600" data-testid="signup-email-error">
                {errors.email}
              </div>
            )}
          </div>
          <div>
            <label className="text-[12px] font-semibold uppercase tracking-[0.14em] text-zinc-600">
              Password
            </label>
            <input
              data-testid="signup-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-black/[0.08] bg-white px-4 py-3 text-[14px] text-zinc-900 outline-none transition-colors focus:border-zinc-900"
              placeholder="At least 8 characters"
            />
            {errors.password && (
              <div className="mt-1 text-[12px] text-rose-600" data-testid="signup-password-error">
                {errors.password}
              </div>
            )}
          </div>

          <button
            type="submit"
            data-testid="signup-submit"
            disabled={submitting}
            className="group inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-black text-[14px] font-semibold text-white transition-all hover:bg-zinc-800 disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Create account
                <ArrowRight
                  className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                  strokeWidth={2.4}
                />
              </>
            )}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
          <div className="h-px flex-1 bg-black/[0.06]" />
          or
          <div className="h-px flex-1 bg-black/[0.06]" />
        </div>

        <button
          type="button"
          data-testid="signup-google"
          onClick={loginWithGoogle}
          className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border border-black/[0.12] bg-white text-[14px] font-semibold text-zinc-900 transition-colors hover:bg-zinc-50"
        >
          <GoogleGlyph />
          Continue with Google
        </button>

        <div className="mt-7 text-center text-[13px] text-zinc-500">
          Already have an account?{" "}
          <Link
            to={`/login${from !== "/boardroom" ? `?from=${encodeURIComponent(from)}` : ""}`}
            data-testid="signup-login-link"
            className="font-semibold text-zinc-900 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-900"
          >
            Sign in →
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34 6.5 29.3 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 19 13 24 13c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34 6.5 29.3 4.5 24 4.5 16.3 4.5 9.6 8.6 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 43.5c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.4 34.8 26.9 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.3 16.2 43.5 24 43.5z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.4l6.2 5.2C40.3 36.7 43.5 31.1 43.5 24c0-1.2-.1-2.4-.4-3.5z"/>
    </svg>
  );
}
